// W37b (2026-10-20): per-rep table for the real Team Live / Executive
// Live bodies. Row shape READ from services/api-server/sinks/team-live-
// sink.mjs buildRepRollup(): {userId, ownerId, email, name, firstName,
// lastName, touches: {total, byType: {call, note, task, text, email}},
// lastTouchAt, activeToday}. The same rows come back as
// `summary.byRep` on the executive route.
//
// What this table does NOT show: presence (clock-in, breaks, on a call),
// credited sales or open work. None of those has a composed source on
// this deployment (presence.available === false, reason
// NO_PRESENCE_SOURCE, READ), so the columns are HubSpot engagement
// counts for today's UTC day and nothing else.
import type { ReactNode } from 'react';

export interface RepRollupRow {
  userId: string;
  ownerId: string | null;
  email: string | null;
  name: string | null;
  touches: { total: number; byType: { call: number; note: number; task: number; text: number; email: number } };
  lastTouchAt: string | null;
  activeToday: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/** Map `reps[]` / `byRep[]` defensively; rows without a user id are dropped. */
export function mapRepRollup(raw: unknown): RepRollupRow[] {
  const list = Array.isArray(raw) ? raw : [];
  const out: RepRollupRow[] = [];
  for (const row of list) {
    if (!isRecord(row)) continue;
    const userId = readString(row.userId);
    if (!userId) continue;
    const touches = isRecord(row.touches) ? row.touches : {};
    const byType = isRecord(touches.byType) ? touches.byType : {};
    out.push({
      userId,
      ownerId: readString(row.ownerId),
      email: readString(row.email),
      name: readString(row.name),
      touches: {
        total: readCount(touches.total),
        byType: {
          call: readCount(byType.call),
          note: readCount(byType.note),
          task: readCount(byType.task),
          text: readCount(byType.text),
          email: readCount(byType.email),
        },
      },
      lastTouchAt: readString(row.lastTouchAt),
      activeToday: row.activeToday === true,
    });
  }
  return out;
}

/** Snapshot timestamp for the header / footer; the ISO string itself when unparsable. */
export function formatSnapshot(iso: string | null): string {
  if (!iso) return 'Snapshot time unknown';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatTouchTime(iso: string | null): string {
  if (!iso) return 'No touch today';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export interface RepRollupTableProps {
  reps: RepRollupRow[];
  title: string;
  subtitle: string;
  dataComponent: string;
}

export function RepRollupTable({ reps, title, subtitle, dataComponent }: RepRollupTableProps): ReactNode {
  return (
    <section className="n-card" data-component={dataComponent} data-testid={dataComponent}>
      <div className="n-head">
        <h2>{title}</h2>
        <small>{subtitle}</small>
      </div>
      {reps.length === 0 ? (
        <div className="n-body">
          <p role="status" data-testid={`${dataComponent}-empty`}>No HubSpot owners found.</p>
        </div>
      ) : (
        <table className="n-table">
          <thead>
            <tr>
              <th>Rep</th>
              <th>Today</th>
              <th>Touches</th>
              <th>Calls · notes · tasks</th>
              <th>Last touch</th>
            </tr>
          </thead>
          <tbody>
            {reps.map(rep => (
              <tr key={rep.userId} data-testid={`${dataComponent}-row`}>
                <td>
                  <strong>{rep.name ?? rep.email ?? `HubSpot user ${rep.userId}`}</strong>
                  {rep.email && rep.name ? <small>{rep.email}</small> : null}
                </td>
                <td>{rep.activeToday ? 'Active today' : 'No activity today'}</td>
                <td>{rep.touches.total}</td>
                <td>
                  {rep.touches.byType.call} · {rep.touches.byType.note} · {rep.touches.byType.task}
                </td>
                <td>{formatTouchTime(rep.lastTouchAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="n-body">
        <p>
          HubSpot engagements created today (UTC day). Presence, credited sales and open work are not
          connected on this deployment.
        </p>
      </div>
    </section>
  );
}
